import { FileText } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockTable, MockRow,
} from "../shared";

export default function PagesDetailSection() {
  return (
    <div data-testid="guide-pages-detail">
      <PageHeading
        icon={FileText}
        title="Action Item Detail"
        description="The complete view of a single action item. Everything about it sits on one page — the instruction, the people, the sub-tasks, the full history, and the buttons to act on it."
      />

      <Callout variant="info" title="In plain words">
        If the other pages are <span className="text-white">lists</span>, this is the <span className="text-white">file folder for one item</span>. Open it to read every detail, see who has held it over time, and do things to it like add a note, hand it over, or close it.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>Whenever you click an item from Summary, Updates, Origins, or anywhere else.</li>
          <li>To act on one item — add an update, hand it off, return it, close it.</li>
          <li>To read the full history of who did what and when.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Action Item Detail"
          legend={[
            <>The <span className="text-white">header and metadata panel</span> show the title, status, lane, priority, due date, owner, who it's currently with, origin, and whether sign-off and review are required.</>,
            <>The <span className="text-white">Sub-Tasks</span> and <span className="text-white">Add Update</span> areas let you break work down and record progress.</>,
            <>The <span className="text-white">Responsibility Timeline</span> (for handed-off items) and the <span className="text-white">Activity timeline</span> show the full history — every holder, status change, sign-off, return, dispatch, and email.</>,
            <>The <span className="text-white">admin controls</span> — status selector, hand to, return, sign off, close, cancel, delete — are where admins act on the item.</>,
          ]}
        >
          <MockRow>
            <MockField label="Title" value="Q3 portfolio pack" marker={1} />
            <MockSelect label="Status" value="In Progress" />
          </MockRow>
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3 space-y-2">
            <p className="text-2xs uppercase tracking-wide text-white/45">Sub-tasks &amp; updates</p>
            <MockField label="Add a progress note" placeholder="What did you do?" marker={2} />
          </div>
          <MockTable
            columns={["Held by", "From → To", "Days held", "Days late"]}
            rows={[
              ["Sara K.", "1 May → 12 May", "11", "0"],
              ["Amal R.", "12 May → now", "9", <span className="text-red-300">+1</span>],
            ]}
          />
          <MockRow>
            <MockButton label="Hand to" marker={4} variant="ghost" />
            <MockButton label="Close task" variant="ghost" />
          </MockRow>
        </Diagram>
        <Steps
          steps={[
            <>Read the <span className="text-white">header and metadata</span> to understand the item at a glance. Super admins also see a <span className="text-white">Revise Due Date</span> button with a reason dropdown.</>,
            <>Use <span className="text-white">Sub-Tasks</span> to break work down, and the <span className="text-white">Add Update</span> form to record progress (open to admins, members, and sub-task assignees).</>,
            <>Scroll the <span className="text-white">timelines</span> to see the whole story — the Responsibility Timeline shows each holder and how long they held it; the Activity timeline lists every event newest-first.</>,
            <>If you're an admin, use the <span className="text-white">controls</span> to change status, hand off, return, sign off, or close.</>,
          ]}
        />
      </Section>

      <Section title="The form walkthroughs all start here">
        <p className="text-xs text-white/60 leading-relaxed">
          The detail page is where individual items get acted on, so every form how-to applies here:
        </p>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1 ml-2 mt-2">
          <li><Link href={pathFor("forms/update")} className="text-cyan-300 hover:underline">Add a Progress Update</Link></li>
          <li><Link href={pathFor("forms/handoff")} className="text-cyan-300 hover:underline">Hand Off Responsibility</Link></li>
          <li><Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return for Amendments</Link></li>
          <li><Link href={pathFor("forms/sub-task")} className="text-cyan-300 hover:underline">Create / Complete a Sub-Task</Link></li>
        </ul>
      </Section>

      <Callout variant="info">
        Items in <span className="text-yellow-300">Awaiting Dispatch</span> show a held-time indicator and a Mark Dispatched call-to-action at the top of the page (admin only).
      </Callout>
    </div>
  );
}
